import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';
import { FaArrowLeft } from 'react-icons/fa';
import { getMirrorStars, setMirrorStars, MirrorStarsRow } from '../../../api/AdminAPI';
import { ADMIN_DASHBOARD_PATH } from 'router/adminPath';
import { botLabel, ownerLabel } from '../botLabels';
import StarsSettlementPanel from '../StarsSettlement';
import { switchSx } from 'utils/chackra/controlStyles';

/**
 * Decide which bots may accept Telegram Stars.
 *
 * Spec: specs/active/2026-09-02-mirror-stars-enablement (US1, US2; plan step
 * 7). The settlement report that renders below this list is a separate screen
 * — `../StarsSettlement` — sharing the route but none of this state.
 *
 * Four properties this screen exists to hold, all of them about money:
 *
 * - **A failed read is never an empty table.** An empty list reads as "no bots
 *   exist", which is the wrong conclusion for an administrator deciding who may
 *   take payments. A load failure replaces the table with the reason.
 * - **A failed write never flips the switch.** The row is updated from what the
 *   server says it stored, not from what was asked for — so a refusal (HTTP
 *   error *or* the in-band `status: "error"` the API answers with HTTP 200)
 *   leaves the row exactly as it was.
 * - **Enabling states where the money goes.** Stars paid to a bot land in that
 *   bot's owner's Telegram balance; we credit the customer's IPScore balance
 *   ourselves. The confirmation says so, names the owner, and is never skipped
 *   — not even when the owner is unknown. Disabling asks nothing: withdrawing
 *   a permission cannot cost anything.
 * - **The delay is stated.** Bots read the flag off their existing mirror-list
 *   poll (~60s), so a change is not instant. Saying so beats an admin
 *   concluding it did not work and toggling twice.
 *
 * Paging is server-side (`limit`/`offset`/`total`): the fleet is far past what
 * one page should carry, and fetching everything to slice it in the browser
 * would make the screen slower the more partners we have.
 */

/** Rows per page. The endpoint's own default; it clamps anything above 200. */
const PAGE_SIZE = 50;

/**
 * Bots apply the flag on their next mirror-list poll
 * (`POOL_MIRRORS_RECONCILE_INTERVAL`, default 60s), so every message about a
 * saved change says "within about a minute" and never implies immediacy.
 */
const PROPAGATION_NOTE =
  'Bots pick the change up on their next refresh — within about a minute, not instantly.';

const StatusBadge = ({ status }: { status: number }) => {
  if (status === 2) return <Badge colorScheme="green">Active</Badge>;
  if (status === 0) return <Badge colorScheme="gray">Inactive</Badge>;
  // Not guessed at: an unmapped code is shown as itself rather than labelled
  // "unknown", which would hide a status this screen simply has not learnt.
  return <Badge colorScheme="yellow">Status {status}</Badge>;
};

const MirrorStarsPanel = () => {
  const navigate = useNavigate();
  const apiKey = useSelector((state: RootState) => state.userData.apiKey);

  const [mirrors, setMirrors] = useState<MirrorStarsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  // Load and write failures are separate slots on purpose: a failed toggle
  // reloads the list, and one shared slot would let that reload wipe the
  // refusal the administrator still has to read.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [pending, setPending] = useState<MirrorStarsRow | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    const res = await getMirrorStars(apiKey, PAGE_SIZE, offset);
    if (res.success && res.data) {
      setMirrors(res.data.mirrors);
      setTotal(res.data.total);
      setLoadError(null);
    } else {
      // Nothing rendered as a list here — see the header comment. The table is
      // replaced by the reason, so "no rows" can never be read as "no bots".
      setMirrors([]);
      setTotal(0);
      setLoadError(res.error || 'Could not load the bot list');
    }
    setLoading(false);
  }, [apiKey, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const applyToggle = useCallback(
    async (row: MirrorStarsRow, enabled: boolean) => {
      if (!apiKey) return;
      setSavingId(row.id);
      setActionError(null);
      setActionNotice(null);

      const res = await setMirrorStars(apiKey, row.id, enabled);

      if (res.success && res.data) {
        const stored = res.data.stars_enabled;
        // Reconciled against storage, never optimistic: the switch shows what
        // the server reported back, which is not necessarily what was asked.
        setMirrors((rows) =>
          rows.map((r) => (r.id === row.id ? { ...r, stars_enabled: stored } : r))
        );
        setActionNotice(
          `Stars ${stored ? 'enabled' : 'disabled'} for ${botLabel(row)}. ${PROPAGATION_NOTE}`
        );
      } else if (res.notFound) {
        setActionError(
          `${botLabel(row)} no longer exists — nothing was changed. The list was out of date and has been reloaded.`
        );
        await load();
      } else {
        // The row keeps its previous value: it was never flipped.
        setActionError(
          res.error ||
            `Could not save the Stars setting for ${botLabel(row)}; it is unchanged.`
        );
      }

      setSavingId(null);
    },
    [apiKey, load]
  );

  /**
   * Switching ON asks first — the confirmation is where we say whose balance
   * the Stars land in. Switching OFF applies straight away.
   */
  const onSwitch = (row: MirrorStarsRow, next: boolean) => {
    if (next) {
      setPending(row);
      return;
    }
    applyToggle(row, false);
  };

  const confirmEnable = async () => {
    const row = pending;
    setPending(null);
    if (row) await applyToggle(row, true);
  };

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex justify="space-between" align="center" mb={2} gap={2} wrap="wrap">
        <Heading size="lg">Telegram Stars per bot</Heading>
        <Button
          leftIcon={<FaArrowLeft />}
          variant="outline"
          size="sm"
          onClick={() => navigate(ADMIN_DASHBOARD_PATH)}
        >
          Dashboard
        </Button>
      </Flex>

      <Text fontSize="sm" color="text.secondary" mb={6}>
        Which bots may accept Telegram Stars. {PROPAGATION_NOTE}
      </Text>

      {actionError && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {actionError}
        </Alert>
      )}
      {actionNotice && (
        <Alert status="success" mb={4} borderRadius="md">
          <AlertIcon />
          {actionNotice}
        </Alert>
      )}

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      ) : loadError ? (
        // Deliberately NOT an empty table — see the header comment.
        <Box>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {loadError}
          </Alert>
          <Button mt={4} size="sm" onClick={load}>
            Try again
          </Button>
        </Box>
      ) : (
        <Box>
          <Box overflowX="auto">
            <Table
              variant="simple"
              size="sm"
              aria-label="Bots and their Stars permission"
            >
              <Thead>
                <Tr>
                  <Th>Bot</Th>
                  <Th>Owner</Th>
                  <Th>Status</Th>
                  <Th>Stars</Th>
                </Tr>
              </Thead>
              <Tbody>
                {mirrors.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <Text fontWeight="semibold" fontSize="sm">
                        {botLabel(row)}
                      </Text>
                      <Text fontSize="xs" color="text.secondary">
                        Bot ID: {row.bot_id ?? '—'}
                      </Text>
                    </Td>
                    <Td fontSize="sm">{ownerLabel(row.user_id)}</Td>
                    <Td>
                      <StatusBadge status={row.status} />
                    </Td>
                    <Td>
                      <Flex align="center" gap={3}>
                        <Switch
                          sx={switchSx}
                          isChecked={row.stars_enabled}
                          isDisabled={savingId === row.id}
                          onChange={(e) => onSwitch(row, e.target.checked)}
                          aria-label={`Stars for ${botLabel(row)}`}
                        />
                        {/* A label as well as the switch: state must never be
                            carried by colour or position alone. */}
                        <Badge colorScheme={row.stars_enabled ? 'green' : 'gray'}>
                          {row.stars_enabled ? 'On' : 'Off'}
                        </Badge>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {mirrors.length === 0 && (
            <Text fontSize="sm" color="text.secondary" mt={4}>
              No bots match this page.
            </Text>
          )}

          <Flex justify="space-between" align="center" mt={4} gap={2} wrap="wrap">
            <Text fontSize="sm" color="text.secondary">
              Showing {showingFrom}-{showingTo} of {total}
            </Text>
            <Flex gap={2}>
              <Button
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                isDisabled={offset === 0}
              >
                Back
              </Button>
              <Button
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                isDisabled={offset + PAGE_SIZE >= total}
              >
                Forward
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      <Divider my={8} />

      {/* Its own screen, rendered here rather than behind a second route: the
          two are read together, but they share no state. */}
      <StarsSettlementPanel />

      <AlertDialog
        isOpen={pending !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => setPending(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Enable Stars for {pending ? botLabel(pending) : ''}?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>
                Owner: <strong>{ownerLabel(pending?.user_id)}</strong>
              </Text>
              <Text mb={3}>
                Stars paid to this bot go to that owner's Telegram balance — not
                to us. The customer's IPScore balance is still credited by us.
              </Text>
              {pending &&
                (pending.user_id === null || pending.user_id === undefined) && (
                  <Alert status="warning" mb={3} borderRadius="md">
                    <AlertIcon />
                    We cannot tell who owns this bot. Whoever does will receive
                    the Stars.
                  </Alert>
                )}
              <Text fontSize="sm" color="text.secondary">
                {PROPAGATION_NOTE}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={confirmEnable}>
                Enable Stars
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default MirrorStarsPanel;
